import productOptionStockStatus from "./productOptionStockStatus";
import { COLLECTION_FILTERS } from "../../helpers/collectionFilter";

export const STOCK_FILTERS = {
  all: "all",
  readyToShip: "readyToShip",
  madeForYou: "madeForYou",
  ...COLLECTION_FILTERS,
};

export const READY_TO_SHIP_STATUSES = ["inStock", "lowStock"];
export const MADE_FOR_YOU_STATUSES = ["madeToOrder"];

// `all` is absent from both so it falls through to "no filtering" in
// filterVariantsByStock
const filterStatuses = {
  [STOCK_FILTERS.readyToShip]: READY_TO_SHIP_STATUSES,
  [STOCK_FILTERS.madeForYou]: MADE_FOR_YOU_STATUSES,
};

// Boolean metafields arrive as "true"/"false", or null when unset
const isSaleVariant = (variant) =>
  variant.isSaleCollection === "true" ||
  variant.isFinalSaleCollection === "true";

const isContractVariant = (variant) => variant.isContractCollection === "true";

const filterMatchers = {
  [STOCK_FILTERS.sale]: isSaleVariant,
  [STOCK_FILTERS.contract]: isContractVariant,
};

/**
 * Stock status of every variant, keyed by variant id.
 *
 * Computed once per variant list so the filter, the segment availability check
 * and the per-option-value availability memo all read the same map instead of
 * each re-running the stockMessage matrix.
 *
 * @returns {Map<string, string>} variant id → status from productOptionStockStatus
 */
export function variantStockStatuses({ variants, quantity }) {
  return new Map(
    variants.map((variant) => [
      variant.id,
      productOptionStockStatus({ variant, quantity }).status,
    ]),
  );
}

/**
 * @param {Map<string, string>} statuses from variantStockStatuses. Sale and
 *   contract go by the variant's own flags and don't need it.
 * @returns {Array} variants matching the filter; every variant for `all`
 */
export function filterVariantsByStock({ variants, statuses, filter }) {
  const matcher = filterMatchers[filter];

  if (matcher) return variants.filter(matcher);

  const allowed = filterStatuses[filter];

  if (!allowed) return variants;

  return allowed.length === 1
    ? variants.filter((variant) => statuses.get(variant.id) === allowed[0])
    : variants.filter((variant) => allowed.includes(statuses.get(variant.id)));
}

/**
 * Which filters have at least one matching variant, so empty segments can be
 * dropped from the toggle rather than rendered dead.
 *
 * @returns {Set<string>} STOCK_FILTERS values
 */
export function availableStockFilters({ variants, statuses }) {
  const available = new Set();

  if (variants.length) available.add(STOCK_FILTERS.all);

  variants.forEach((variant) => {
    const status = statuses.get(variant.id);

    if (isSaleVariant(variant)) available.add(STOCK_FILTERS.sale);

    if (isContractVariant(variant)) available.add(STOCK_FILTERS.contract);

    if (READY_TO_SHIP_STATUSES.includes(status))
      available.add(STOCK_FILTERS.readyToShip);

    if (MADE_FOR_YOU_STATUSES.includes(status))
      available.add(STOCK_FILTERS.madeForYou);
  });

  return available;
}

/**
 * Variant sharing the most option values with the current selection, used to
 * land somewhere sensible when a filter change hides the selected variant.
 * Ties resolve to the earliest variant.
 *
 * @returns {Object|null} null when there are no variants to choose from
 */
export function pickClosestVariant({ variants, selectedOptionValues }) {
  let closest = null;
  let closestScore = -1;

  variants.forEach((variant) => {
    const score = variant.selectedOptions.filter(
      ({ name, value }) => selectedOptionValues[name] === value,
    ).length;

    if (score > closestScore) {
      closestScore = score;
      closest = variant;
    }
  });

  return closest;
}
